import { useState, useCallback } from 'react';
import { message } from 'antd';
import { p922aApi, F921, F9211 } from '../../../services/p922aApi';
import { useAuth } from '../../../contexts/AuthContext';
import { useFieldErrors } from '../../../hooks/useFieldErrors';
import { Mode } from './useP922AMode';

export function useP922ARecord() {
  const { user } = useAuth();
  const [record, setRecord] = useState<F921 | null>(null);
  const [formData, setFormData] = useState<Partial<F921>>({});
  const [details, setDetails] = useState<Partial<F9211>[]>([]);
  const [selectedRow, setSelectedRow] = useState(0);
  const [loading, setLoading] = useState(false);
  const { fieldErrors, setFieldErrors, clearFieldErrors, getFieldError, hasErrors } = useFieldErrors();

  /** Load a single record by ID */
  const loadRecord = useCallback(async (id: string): Promise<{
    data: F921;
    dets: Partial<F9211>[];
  } | null> => {
    setLoading(true);
    try {
      const data = await p922aApi.get(id);
      setRecord(data);
      setFormData({ ...data });
      const dets = data.details || [];
      setDetails(dets);
      setSelectedRow(0);
      return { data, dets };
    } catch {
      message.error('載入失敗');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /** Set a single master field */
  const setField = useCallback((key: keyof F921, value: F921[keyof F921]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  /** Get a single master field value */
  const getField = useCallback((key: keyof F921): F921[keyof F921] => {
    return (formData as Record<string, F921[keyof F921]>)[key] ?? '';
  }, [formData]);

  /** Get a detail field value from the selected row */
  const getDetailField = useCallback((key: keyof F9211): F9211[keyof F9211] => {
    const sel = details[selectedRow] as Partial<F9211> | undefined;
    return sel ? (sel as Record<string, F9211[keyof F9211]>)[key] ?? '' : '';
  }, [details, selectedRow]);

  /** Set a detail field value on the selected row */
  const setDetailField = useCallback((key: keyof F9211, value: F9211[keyof F9211]) => {
    setDetails(prev => {
      const next = [...prev];
      if (next[selectedRow]) {
        next[selectedRow] = { ...next[selectedRow], [key]: value };
      }
      return next;
    });
  }, [selectedRow]);

  /** Save (create or update) */
  const handleSave = useCallback(async (
    mode: Mode,
    onSuccess: (result: F921) => void,
  ) => {
    setLoading(true);
    try {
      const req = {
        master: formData as F921,
        details: details as F9211[],
      };
      if (mode === 'add') {
        const result = await p922aApi.create(req);
        message.success(`新增成功: ${result.f921_01}`);
        onSuccess(result);
      } else {
        const result = await p922aApi.update(formData.f921_01!, req);
        message.success('修改成功');
        setRecord(result);
        setFormData({ ...result });
        setDetails(result.details || []);
        onSuccess(result);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { errors?: { field: string; message: string }[]; error?: string } } };
      const errors = err.response?.data?.errors;
      if (errors && Array.isArray(errors)) {
        setFieldErrors(errors);
        const summary = errors.slice(0, 5).map(e => e.message).join('\n');
        const extra = errors.length > 5 ? `\n...共 ${errors.length} 項` : '';
        message.error({ content: summary + extra, duration: 6, style: { whiteSpace: 'pre-line' } });
      } else {
        message.error(err.response?.data?.error || '儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [formData, details, setFieldErrors]);

  /** Delete the current record */
  const handleDelete = useCallback(async (): Promise<boolean> => {
    if (!record) return false;
    try {
      await p922aApi.delete(record.f921_01);
      message.success('刪除成功');
      return true;
    } catch {
      message.error('刪除失敗');
      return false;
    }
  }, [record]);

  /** Reset form to current record (revert) */
  const revertToRecord = useCallback(() => {
    clearFieldErrors();
    if (record) {
      setFormData({ ...record });
      setDetails(record.details || []);
    }
  }, [record, clearFieldErrors]);

  /** Reset form for a new add */
  const resetForAdd = useCallback(() => {
    clearFieldErrors();
    setRecord(null);
    const today = new Date().toISOString().substring(0, 10) + 'T00:00:00Z';
    setFormData({
      f921_02: user?.userId || '',
      f921_11: today,
      f921_13: today,
      f921_15: today,
      f921_91: user?.userId || '',
      f921_18: '日',
    });
    setDetails([]);
    setSelectedRow(0);
  }, [user, clearFieldErrors]);

  /** Apply result after save */
  const applyResult = useCallback((result: F921) => {
    setRecord(result);
    setFormData({ ...result });
    setDetails(result.details || []);
  }, []);

  return {
    record, formData, details, selectedRow, loading,
    setSelectedRow, setDetails,
    loadRecord, setField, getField,
    getDetailField, setDetailField,
    handleSave, handleDelete,
    revertToRecord, resetForAdd, applyResult,
    fieldErrors, getFieldError, clearFieldErrors, hasErrors,
  };
}
